import { useRef, useCallback, KeyboardEvent } from 'react';

interface RatingInputProps {
  value: number | null;
  onChange: (rating: number | null) => void;
}

const RATINGS = [1, 2, 3, 4, 5] as const;

export function RatingInput({ value, onChange }: RatingInputProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const focusedIndex = value ? value - 1 : 0;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = value ? value - 1 : 0;
      let nextIndex: number | null = null;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          nextIndex = currentIndex < RATINGS.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : RATINGS.length - 1;
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          onChange(null);
          return;
        default:
          return;
      }

      if (nextIndex !== null) {
        const nextValue = RATINGS[nextIndex] as number;
        onChange(nextValue);
        const items = groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]');
        items?.[nextIndex]?.focus();
      }
    },
    [value, onChange]
  );

  const handleClick = (rating: number) => {
    if (value === rating) {
      onChange(null);
    } else {
      onChange(rating);
    }
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Rating"
      className="rating-input"
      onKeyDown={handleKeyDown}
    >
      {RATINGS.map((rating, index) => {
        const isChecked = value === rating;
        const isFocusable = index === focusedIndex;

        return (
          <span
            key={rating}
            role="radio"
            aria-checked={isChecked}
            aria-label={`${rating} out of 5`}
            tabIndex={isFocusable ? 0 : -1}
            className={`rating-input-item${isChecked ? ' rating-input-item--selected' : ''}`}
            onClick={() => handleClick(rating)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(rating);
              }
            }}
          >
            {rating}
          </span>
        );
      })}
    </div>
  );
}
